import { useMemo, VFC } from 'react';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { filterUsableObjects, UsableObjectConfig } from 'dashboard/lib/workflows';
import { ListStatus } from 'dashboard/models/list';
import { ProductFeatureName } from 'dashboard/models/organization';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import { WorkflowActionAddItemToListFaceV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

interface ParamsAddItemToListFaceV1Props {
  lists: WorkflowActionMeta['lists'];
  editable: boolean;
  errors: WorkflowError[];
  usableObjects: UsableObjectConfig[];
  params: WorkflowActionAddItemToListFaceV1['params'];
  focusStep: (token: string) => void;
  onMenuClose: () => void;
  updateActionParams: (newParams: WorkflowActionAddItemToListFaceV1['params']) => void;
}

const ParamsAddItemToListFaceV1: VFC<ParamsAddItemToListFaceV1Props> = ({
  lists,
  editable,
  errors,
  usableObjects,
  params,
  focusStep,
  onMenuClose,
  updateActionParams,
}) => {
  const hasListPerm = usePermission(Permission.ListView);
  const allowUntrackableSources = useProductFeature(
    ProductFeatureName.ListsFacesUntrackableSources,
  );
  const isTemplateAvailable = useMemo(() => {
    return !params['list-id'] || lists.map((list) => list.id).includes(params['list-id']);
  }, [lists, params]);
  const possibleObjects = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [
          WorkflowFieldObjectKey.Inquiry,
          ...(allowUntrackableSources ? [WorkflowFieldObjectKey.Account] : []),
        ],
        excludeNonPersonaObjects: true,
      },
    });
  }, [allowUntrackableSources, usableObjects]);

  const selectObject = (object: WorkflowActionAddItemToListFaceV1['params']['object']) => {
    updateActionParams({
      ...params,
      object,
    });
  };

  const selectList = (list: WorkflowActionAddItemToListFaceV1['params']['list-id']) => {
    updateActionParams({
      ...params,
      'list-id': list,
    });
  };

  const availableLists =
    lists
      ?.filter(
        (list) =>
          list.attributes['status'] === ListStatus.Active && list.type === ApiModel.ListFace,
      )
      .map((list) => ({
        label: list.name,
        value: list.id,
      })) || [];

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionAddItemToListFaceV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Object' htmlFor='params-add-item-to-list-face__object-input' />
      <ReactSelect
        value={params.object}
        options={possibleObjects}
        onChange={selectObject}
        id='params-add-item-to-list-face__object'
        disabled={!editable}
        hasError={hasErrorFor('object')}
        onFocusOption={focusStep}
        onMenuClose={onMenuClose}
      />
      {renderErrorMessageFor('object')}
      <Spacer height={2} />
      <ParamLabel
        labelText='List'
        htmlFor='params-add-item-to-list-face__list-input'
        helpText={
          hasListPerm
            ? isTemplateAvailable
              ? undefined
              : 'The current list is not available to you at this time.'
            : 'Your role does not have permissions to view lists.'
        }
      />
      <ReactSelect
        value={params['list-id']}
        options={availableLists}
        onChange={selectList}
        disabled={!editable || !hasListPerm}
        placeholder={hasListPerm ? (isTemplateAvailable ? undefined : 'UNAVAILABLE') : 'RESTRICTED'}
        hasError={hasErrorFor('list-id')}
        id='params-add-item-to-list-face__list'
      />
      {renderErrorMessageFor('list-id')}
    </>
  );
};

export default ParamsAddItemToListFaceV1;
